import { useMemo } from 'react';
import { useSpeciesLifecycleOptional } from './SpeciesLifecycleContext';
import { animalToLifecycleParams } from './animalLifecycleParams';

/**
 * Resolve lifecycle tier labels for an animal using shared species config.
 *
 * @param {object|null|undefined} animal
 * @param {object} [overrides] - optional speciesSlug, gender, ageMonths, breedingStatus, isPregnant
 */
export function useAnimalLifecycleLabels(animal, overrides = {}) {
  const lifecycle = useSpeciesLifecycleOptional();
  const base = useMemo(() => animalToLifecycleParams(animal), [animal]);

  const params = useMemo(
    () => ({
      speciesSlug: overrides.speciesSlug ?? base.speciesSlug,
      gender: overrides.gender ?? base.gender,
      ageMonths: overrides.ageMonths ?? base.ageMonths,
      breedingStatus: overrides.breedingStatus ?? base.breedingStatus,
      isPregnant: overrides.isPregnant ?? base.isPregnant,
    }),
    [
      base.speciesSlug,
      base.gender,
      base.ageMonths,
      base.breedingStatus,
      base.isPregnant,
      overrides.speciesSlug,
      overrides.gender,
      overrides.ageMonths,
      overrides.breedingStatus,
      overrides.isPregnant,
    ]
  );

  const labels = useMemo(() => {
    if (!lifecycle?.isReady) return [];
    return lifecycle.resolveTierLabels(params);
  }, [lifecycle, params]);

  return {
    labels,
    params,
    isReady: lifecycle?.isReady ?? false,
    isLoading: lifecycle?.isLoading ?? false,
    getTierName: lifecycle?.getTierName,
    listTierNamesForSpecies: lifecycle?.listTierNamesForSpecies,
  };
}
